import axios from "axios";
import { getTokenHeader } from "@/helpers/commonHelper";
import { normalizeHtmlForDiff } from "@/helpers/textDiff";
import {
  fetchPromptCandidatesByAction,
  mapPromptOption
} from "@/helpers/promptResolutionHelper";

const GENERAL_FIELDS = [
  {
    key: "content",
    label: "Textblock 1 (Allgemein)",
    get: item => item.content
  },
  {
    key: "content_2",
    label: "Textblock 2 (Allgemein)",
    get: item => item.content_2
  },
  {
    key: "content_3",
    label: "Textblock 3 (Allgemein)",
    get: item => item.content_3
  }
];

const channelFieldDefs = (channelKey, channelLabel) => [
  {
    key: `alternative_content.${channelKey}`,
    label: `Textblock 1 (${channelLabel})`,
    get: item => item.alternative_content?.[channelKey]
  },
  {
    key: `alternative_content_2.${channelKey}`,
    label: `Textblock 2 (${channelLabel})`,
    get: item => item.alternative_content_2?.[channelKey]
  },
  {
    key: `alternative_content_3.${channelKey}`,
    label: `Textblock 3 (${channelLabel})`,
    get: item => item.alternative_content_3?.[channelKey]
  }
];

const CHANNEL_ICONS = {
  general: "mdi-text-box-outline",
  gemeindeblatt: "mdi-book-open-page-variant-outline",
  web: "mdi-web",
  app: "mdi-cellphone"
};
const DEFAULT_CHANNEL_ICON = "mdi-share-variant-outline";

const buildSavePayload = changes => {
  const payload = {};
  const nested = {};
  changes.forEach(change => {
    if (change.fieldKey.includes(".")) {
      const [base, key] = change.fieldKey.split(".");
      nested[base] = nested[base] || {};
      nested[base][key] = change.after;
    } else {
      payload[change.fieldKey] = change.after;
    }
  });
  Object.keys(nested).forEach(base => {
    payload[base] = nested[base];
  });
  return payload;
};

export default {
  name: "BatchRewriteDialog",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    excludedLockedCount: {
      type: Number,
      default: 0
    },
    excludedDeletedCount: {
      type: Number,
      default: 0
    },
    principalId: {
      type: String,
      required: true
    },
    apiUrl: {
      type: String,
      required: true
    },
    newsApiUrl: {
      type: String,
      required: true
    },
    channelsConfig: {
      type: Array,
      default: () => []
    },
    issues: {
      type: Array,
      default: () => []
    },
    mainPeacock: {
      type: String,
      default: "#00636C"
    }
  },
  data() {
    return {
      phase: "config",
      promptsLoading: false,
      promptOptions: [],
      selectedPrompt: null,
      channelChecks: { general: true },
      cancelled: false,
      fastForward: false,
      currentIndex: 0,
      currentStatus: "loading",
      currentChanges: [],
      processed: []
    };
  },
  computed: {
    internalOpen: {
      get() {
        return this.isOpen;
      },
      set(value) {
        if (!value) {
          this.closeDialog();
        }
        this.$emit("update:isOpen", value);
      }
    },
    subline() {
      let text = `${this.items.length} ausgewählte Nachricht${
        this.items.length === 1 ? "" : "en"
      }`;
      const excluded = [];
      if (this.excludedLockedCount) {
        excluded.push(`${this.excludedLockedCount} gesperrt`);
      }
      if (this.excludedDeletedCount) {
        excluded.push(`${this.excludedDeletedCount} gelöscht`);
      }
      if (excluded.length) {
        text += ` · ${excluded.join(" · ")}`;
      }
      return text;
    },
    availableChannels() {
      const keys = new Set();
      this.items.forEach(item =>
        (item.channels || []).forEach(channel => keys.add(channel))
      );
      return this.channelsConfig.filter(channel => keys.has(channel.value));
    },
    channelChips() {
      const chips = [
        { key: "general", label: "Allgemein", icon: CHANNEL_ICONS.general }
      ];
      this.availableChannels.forEach(channel => {
        chips.push({
          key: channel.value,
          label: channel.title,
          icon: CHANNEL_ICONS[channel.value] || DEFAULT_CHANNEL_ICON
        });
      });
      return chips.map(chip => {
        const on = !!this.channelChecks[chip.key];
        return {
          ...chip,
          on,
          chipBg: on ? this.mainPeacock : "#fff",
          chipColor: on ? "#fff" : "#2A2A2C",
          chipBorder: on ? this.mainPeacock : "#CDCDD2",
          toggle: () => this.toggleChannel(chip.key)
        };
      });
    },
    activeFieldDefs() {
      let defs = [];
      if (this.channelChecks.general) {
        defs = defs.concat(GENERAL_FIELDS);
      }
      this.availableChannels.forEach(channel => {
        if (this.channelChecks[channel.value]) {
          defs = defs.concat(channelFieldDefs(channel.value, channel.title));
        }
      });
      return defs;
    },
    currentItem() {
      return this.items[this.currentIndex];
    },
    withChanges() {
      return this.processed.filter(result => result.changes.length);
    },
    noChangeCount() {
      return this.processed.length - this.withChanges.length;
    },
    appliedCount() {
      return this.processed.filter(r => r.decision === "applied").length;
    },
    rejectedCount() {
      return this.processed.filter(r => r.decision === "rejected").length;
    },
    startLabel() {
      return `Umschreiben starten (${this.items.length})`;
    },
    stepLabel() {
      return `Nachricht ${Math.min(
        this.currentIndex + 1,
        this.items.length
      )} von ${this.items.length}`;
    },
    progressPct() {
      return Math.round(
        (this.currentIndex / Math.max(this.items.length, 1)) * 100
      );
    },
    curMeta() {
      if (!this.currentItem) return "";
      const parts = [`${this.currentChanges.length} Änderungsvorschläge`];
      const issueTitle = this.issueTitle(this.currentItem);
      if (issueTitle) parts.push(issueTitle);
      return parts.join(" · ");
    },
    stepDots() {
      return this.items.map((item, i) => {
        const record = this.processed[i];
        if (record) {
          if (record.decision === "applied") return this.mainPeacock;
          if (record.decision === "rejected") return "#BDBDBD";
          return "#E0E1E6";
        }
        return i === this.currentIndex ? "#E9AE33" : "#E0E0E0";
      });
    },
    decidedLabel() {
      return `${this.appliedCount} übernommen · ${this.rejectedCount} verworfen`;
    },
    applyAllRemainingLabel() {
      return "Alle verbleibenden übernehmen";
    },
    doneTitle() {
      return `${this.appliedCount} von ${this.withChanges.length} Vorschlägen übernommen`;
    },
    doneHint() {
      return (
        "Übernommene Texte werden je Kanal gespeichert, verworfene Vorschläge bleiben ohne Wirkung. " +
        `${this.noChangeCount} Nachricht${
          this.noChangeCount === 1 ? "" : "en"
        } hatten keinen Befund.`
      );
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initialize();
      }
    }
  },
  methods: {
    async initialize() {
      this.phase = "config";
      this.cancelled = false;
      this.fastForward = false;
      this.currentIndex = 0;
      this.currentStatus = "loading";
      this.currentChanges = [];
      this.processed = [];
      this._decisionResolve = null;

      const channelChecks = { general: true };
      this.availableChannels.forEach(channel => {
        channelChecks[channel.value] = true;
      });
      this.channelChecks = channelChecks;

      await this.fetchPrompts();
      this.selectDefaultPrompt();
    },

    closeDialog() {
      this.cancelled = true;
      if (this._decisionResolve) {
        const resolve = this._decisionResolve;
        this._decisionResolve = null;
        resolve("cancelled");
      }
      this.emitSavedSummary();
      this.$emit("close");
    },

    emitSavedSummary() {
      const saved = this.processed.filter(r => r.saved);
      if (!saved.length) return;
      this.$emit("saved", {
        savedCount: saved.length,
        items: saved.map(r => ({ ...r.item, ...r.payload }))
      });
    },

    toggleChannel(key) {
      this.channelChecks[key] = !this.channelChecks[key];
    },

    issueTitle(item) {
      return (
        this.issues.find(issue => issue._id === item.issue_id)?.title || ""
      );
    },

    async fetchPromptCandidatesByAction(action) {
      const headers = getTokenHeader();
      return fetchPromptCandidatesByAction({
        apiUrl: this.apiUrl,
        headers,
        principalId: this.principalId,
        module: "news",
        action
      });
    },

    async fetchPrompts() {
      try {
        this.promptsLoading = true;
        let prompts = await this.fetchPromptCandidatesByAction("rewrite");
        if (!prompts.length) {
          prompts = await this.fetchPromptCandidatesByAction(null);
        }
        this.promptOptions = prompts.map(mapPromptOption);
      } catch (e) {
        console.error("Fehler beim Laden der Prompts:", e);
        this.$emit("error", { text: "Keine Prompts gefunden.", error: e });
      } finally {
        this.promptsLoading = false;
      }
    },

    selectDefaultPrompt() {
      const prompts = this.promptOptions;
      if (!Array.isArray(prompts) || !prompts.length) return;
      const def = prompts.find(
        p => p.isDefault && (!p.module || p.module === "news")
      );
      this.selectedPrompt = (def || prompts[0]).id;
    },

    async rewriteText(text, prompt) {
      const headers = getTokenHeader();
      const res = await axios.post(
        `${this.apiUrl}api/eo-prompt/exec`,
        {
          principal_id: this.principalId,
          apiShortId: prompt.apiShortId,
          promptShortId: prompt.promptShortId,
          payload: text
        },
        { headers }
      );
      return res.data?.text || "";
    },

    async persistItem(record) {
      const payload = buildSavePayload(record.changes);
      try {
        const headers = getTokenHeader();
        await axios.put(
          `${this.newsApiUrl}news/`,
          {
            id: record.item._id,
            _id: record.item._id,
            principal_id: record.item.principal_id,
            ...payload
          },
          { headers }
        );
        record.saved = true;
        record.payload = payload;
      } catch (e) {
        console.error(e);
        record.saved = false;
        this.$emit("error", {
          text: `„${record.item.title}“ konnte nicht gespeichert werden.`,
          error: e
        });
      }
    },

    waitForDecision() {
      return new Promise(resolve => {
        this._decisionResolve = resolve;
      });
    },

    resolveDecision(decision) {
      if (!this._decisionResolve) return;
      this.currentStatus = "loading";
      const resolve = this._decisionResolve;
      this._decisionResolve = null;
      resolve(decision);
    },

    applyCurrent() {
      this.resolveDecision("applied");
    },

    rejectCurrent() {
      this.resolveDecision("rejected");
    },

    applyAllRemaining() {
      this.fastForward = true;
      this.resolveDecision("applied");
    },

    cancelRun() {
      this.closeDialog();
    },

    finish() {
      this.closeDialog();
    },

    async startRun() {
      const prompt = this.promptOptions.find(p => p.id === this.selectedPrompt);
      if (!prompt || !prompt.apiShortId || !prompt.promptShortId) {
        this.$emit("error", { text: "Prompt-Konfiguration unvollständig." });
        return;
      }

      this.phase = "processing";
      this.cancelled = false;
      this.fastForward = false;
      this.processed = [];
      this.currentIndex = 0;

      const fieldDefs = this.activeFieldDefs;

      for (let i = 0; i < this.items.length; i++) {
        if (this.cancelled) return;
        this.currentIndex = i;
        this.currentStatus = "loading";
        this.currentChanges = [];
        const item = this.items[i];

        const applicableFields = fieldDefs
          .map(fieldDef => ({ ...fieldDef, before: fieldDef.get(item) || "" }))
          .filter(fieldDef => fieldDef.before.trim().length > 0);

        const rewritten = await Promise.all(
          applicableFields.map(async fieldDef => {
            try {
              const after = await this.rewriteText(fieldDef.before, prompt);
              return { ...fieldDef, after };
            } catch (e) {
              console.error(e);
              return { ...fieldDef, after: fieldDef.before };
            }
          })
        );

        if (this.cancelled) return;

        const changes = rewritten
          .filter(
            fieldDef =>
              normalizeHtmlForDiff(fieldDef.before) !==
              normalizeHtmlForDiff(fieldDef.after)
          )
          .map(fieldDef => ({
            fieldKey: fieldDef.key,
            label: fieldDef.label,
            before: fieldDef.before,
            after: fieldDef.after
          }));

        if (!changes.length) {
          this.processed.push({ item, changes: [], decision: "skipped" });
          continue;
        }

        if (this.fastForward) {
          const record = { item, changes, decision: "applied" };
          await this.persistItem(record);
          this.processed.push(record);
          if (this.cancelled) return;
          continue;
        }

        this.currentChanges = changes;
        this.currentStatus = "reviewing";
        const decision = await this.waitForDecision();
        if (decision === "cancelled") return;

        const record = { item, changes, decision };
        if (decision === "applied") {
          await this.persistItem(record);
        }
        this.processed.push(record);
        if (this.cancelled) return;
      }

      if (this.cancelled) return;
      this.currentIndex = this.items.length;
      this.phase = "done";
    }
  }
};
